import { useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import type { AuthSessionStatus, AuthSessionUser } from '@domain/auth/types';
import { fetchCallBootstrap } from '@domain/call/api';
import { rtcLog } from '@domain/call/debug';
import { isCallMessageType, parseCallMessage } from '@domain/call/message';
import { CallSignalingClient } from '@domain/call/signalingClient';
import { CallRtcSession } from '@domain/call/webrtc';
import type {
  CallBootstrap,
  CallConnectionState,
  CallEnvelope,
  CallPeer,
  CallSession,
  CallType,
  CallUiState,
} from '@domain/call/types';
import type { DirectoryMap, SessionKind, SessionMap } from '@domain/chat/types';
import type { LogEntry } from '@shared/config/runtime';
import { getErrorMessage } from '@shared/utils/ui';
import { eventBus } from '../../../eventBus';
import { getOrCreateSocketDeviceId } from '../../../im/bootstrap';

type UseWorkspaceCallOptions = {
  apiBase: string;
  authStatus: AuthSessionStatus;
  authUser: AuthSessionUser | null;
  contactsRef: MutableRefObject<DirectoryMap>;
  sessionsRef: MutableRefObject<SessionMap>;
  activeSessionKeyRef: MutableRefObject<string | null>;
  pushLog: (msg: string, kind?: LogEntry['kind']) => void;
  sessionKeyOf: (sessionType: SessionKind, sessionId: number) => string;
};

const nextCommandId = () => crypto.randomUUID();

const CALL_SIGNALING_UNAVAILABLE_MESSAGE = '通话服务暂时不可用，请稍后再试';

function isExpectedClosedCallError(error: unknown) {
  const message = getErrorMessage(error);
  return message === 'Call session is closed' || message === 'Call session is no longer active';
}

function attachMediaStream(element: HTMLMediaElement | null, stream: MediaStream | null) {
  if (!element || element.srcObject === stream) {
    return;
  }
  element.srcObject = stream;
  rtcLog('info', 'attach media stream to element', {
    tag: element.tagName,
    muted: element.muted,
    audioTracks: stream?.getAudioTracks().map((track) => track.readyState) ?? [],
    videoTracks: stream?.getVideoTracks().map((track) => track.readyState) ?? [],
  });
  if (stream) {
    void element.play().then(() => {
      rtcLog('info', 'media element play started', {
        tag: element.tagName,
        muted: element.muted,
        paused: element.paused,
        readyState: element.readyState,
        videoWidth: 'videoWidth' in element ? (element as HTMLVideoElement).videoWidth : undefined,
        videoHeight: 'videoHeight' in element ? (element as HTMLVideoElement).videoHeight : undefined,
      });
    }).catch((error) => {
      rtcLog('warn', 'media element play failed, retry muted', {
        tag: element.tagName,
        muted: element.muted,
        error: getErrorMessage(error),
      });
      const wasMuted = element.muted;
      element.muted = true;
      void element.play()
        .then(() => {
          rtcLog('info', 'media element muted retry started', {
            tag: element.tagName,
            paused: element.paused,
            readyState: element.readyState,
          });
          element.muted = wasMuted;
        })
        .catch((retryError) => {
          rtcLog('warn', 'media element muted retry failed', {
            tag: element.tagName,
            error: getErrorMessage(retryError),
          });
          element.muted = wasMuted;
        });
    });
  }
}

function useStreamAudioLevel(stream: MediaStream | null) {
  const [level, setLevel] = useState(0);

  useEffect(() => {
    if (!stream || stream.getAudioTracks().length === 0 || typeof window === 'undefined') {
      setLevel(0);
      return;
    }

    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextCtor) {
      setLevel(0);
      return;
    }

    let cancelled = false;
    let animationFrame = 0;
    const audioContext = new AudioContextCtor();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.72;
    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);
    const samples = new Uint8Array(analyser.fftSize);

    const tick = () => {
      if (cancelled) {
        return;
      }
      analyser.getByteTimeDomainData(samples);
      let total = 0;
      for (const sample of samples) {
        const centered = (sample - 128) / 128;
        total += centered * centered;
      }
      const rms = Math.sqrt(total / samples.length);
      setLevel(Math.min(1, rms * 4));
      animationFrame = window.requestAnimationFrame(tick);
    };

    void audioContext.resume().catch(() => undefined);
    tick();

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(animationFrame);
      source.disconnect();
      analyser.disconnect();
      void audioContext.close().catch(() => undefined);
      setLevel(0);
    };
  }, [stream]);

  return level;
}

export function useWorkspaceCall({
  apiBase,
  authStatus,
  authUser,
  contactsRef,
  sessionsRef,
  activeSessionKeyRef,
  pushLog,
  sessionKeyOf,
}: UseWorkspaceCallOptions) {
  const [callBootstrap, setCallBootstrap] = useState<CallBootstrap | null>(null);
  const [callBootstrapError, setCallBootstrapError] = useState<string | null>(null);
  const [callConnectionState, setCallConnectionState] = useState<CallConnectionState>('idle');
  const [callUiState, setCallUiState] = useState<CallUiState>('idle');
  const [activeCall, setActiveCall] = useState<CallSession | null>(null);
  const [incomingCall, setIncomingCall] = useState<CallSession | null>(null);
  const [callLocalStream, setCallLocalStream] = useState<MediaStream | null>(null);
  const [callRemoteStream, setCallRemoteStream] = useState<MediaStream | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const localAudioLevel = useStreamAudioLevel(callLocalStream);
  const remoteAudioLevel = useStreamAudioLevel(callRemoteStream);

  const callClientRef = useRef<CallSignalingClient | null>(null);
  const callClientSeqRef = useRef(0);
  const callLifecycleSeqRef = useRef(0);
  const rtcSessionRef = useRef<CallRtcSession | null>(null);
  const localCallMediaRef = useRef<HTMLMediaElement | null>(null);
  const remoteCallMediaRef = useRef<HTMLMediaElement | null>(null);
  const callBootstrapRef = useRef<CallBootstrap | null>(null);
  const activeCallRef = useRef<CallSession | null>(null);
  const incomingCallRef = useRef<CallSession | null>(null);
  const incomingRingtoneRef = useRef<HTMLAudioElement | null>(null);
  const leaseTimerRef = useRef<number | null>(null);
  const mediaReportedCallIdRef = useRef<string | null>(null);

  useEffect(() => {
    callBootstrapRef.current = callBootstrap;
  }, [callBootstrap]);

  useEffect(() => {
    activeCallRef.current = activeCall;
  }, [activeCall]);

  useEffect(() => {
    incomingCallRef.current = incomingCall;
  }, [incomingCall]);

  useEffect(() => {
    attachMediaStream(localCallMediaRef.current, callLocalStream);
  }, [activeCall, callLocalStream]);

  useEffect(() => {
    attachMediaStream(remoteCallMediaRef.current, callRemoteStream);
  }, [activeCall, callRemoteStream]);

  const stopIncomingRingtone = useCallback(() => {
    const audio = incomingRingtoneRef.current;
    if (!audio) {
      return;
    }
    audio.pause();
    audio.currentTime = 0;
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!incomingCall) {
      stopIncomingRingtone();
      return;
    }

    const audio = incomingRingtoneRef.current ?? new Audio('/audio/incoming-call.mp3');
    audio.loop = true;
    audio.preload = 'auto';
    incomingRingtoneRef.current = audio;
    void audio.play().catch(() => {
      // Autoplay may be blocked until the page receives a user gesture.
    });

    return () => {
      stopIncomingRingtone();
    };
  }, [incomingCall, stopIncomingRingtone]);

  const resetCallMedia = useCallback(() => {
    rtcSessionRef.current?.close();
    rtcSessionRef.current = null;
    setCallLocalStream(null);
    setCallRemoteStream(null);
  }, []);

  const isCallCurrent = useCallback((callId: string, lifecycleSeq: number) => (
    callLifecycleSeqRef.current === lifecycleSeq
    && activeCallRef.current?.callId === callId
  ), []);

  const finishCall = useCallback((nextState: CallUiState) => {
    callLifecycleSeqRef.current += 1;
    activeCallRef.current = null;
    incomingCallRef.current = null;
    resetCallMedia();
    setActiveCall(null);
    setIncomingCall(null);
    setIsMuted(false);
    setIsVideoEnabled(true);
    setCallUiState(nextState);
    stopIncomingRingtone();
    mediaReportedCallIdRef.current = null;
    if (leaseTimerRef.current !== null) {
      window.clearInterval(leaseTimerRef.current);
      leaseTimerRef.current = null;
    }
  }, [resetCallMedia, stopIncomingRingtone]);

  useEffect(() => eventBus.on('NewMsgEvent', (message) => {
    if (!isCallMessageType(message.msgType)) return;
    const terminalCall = parseCallMessage(message.msgData);
    const currentCall = activeCallRef.current ?? incomingCallRef.current;
    if (!terminalCall || terminalCall.scope !== 'direct' || !currentCall || terminalCall.callId !== currentCall.callId) return;
    finishCall('ended');
  }), [finishCall]);

  const sendCallEvent = useCallback(<T extends Record<string, unknown>>(
    event: string,
    call: CallSession,
    payload: T,
  ) => {
    const client = callClientRef.current;
    if (!client || !call.sessionId || !call.fence || !call.capability) {
      throw new Error('Call signaling is not ready');
    }
    const envelope: CallEnvelope<T> = {
      protocolVersion: 1,
      type: 'signal',
      action: event,
      commandId: nextCommandId(),
      capabilityCommandId: call.commandId,
      callId: call.callId,
      sessionId: call.sessionId,
      fence: call.fence,
      capability: call.capability,
      payload,
    };
    client.send(envelope);
  }, []);

  const sendParticipantReport = useCallback((action: 'media_connected' | 'lease_refresh') => {
    const client = callClientRef.current;
    const call = activeCallRef.current;
    if (!client || !call?.sessionId || !call.fence) return;
    const reportSequence = call.reportSequence + 1;
    const updated = { ...call, reportSequence };
    activeCallRef.current = updated;
    setActiveCall(updated);
    client.send({
      protocolVersion: 1,
      type: 'report',
      action,
      commandId: nextCommandId(),
      callId: call.callId,
      sessionId: call.sessionId,
      fence: call.fence,
      reportSequence,
      payload: {},
    });
  }, []);

  const ensureRtcSession = useCallback(async (call: CallSession) => {
    const bootstrap = callBootstrapRef.current;
    if (!bootstrap) {
      throw new Error('Call bootstrap is missing');
    }
    const lifecycleSeq = callLifecycleSeqRef.current;
    if (!isCallCurrent(call.callId, lifecycleSeq)) {
      throw new Error('Call session is no longer active');
    }
    if (rtcSessionRef.current) {
      return rtcSessionRef.current;
    }
    const rtc = new CallRtcSession(bootstrap, call.type, {
      onIceCandidate: (candidate) => {
        if (!isCallCurrent(call.callId, lifecycleSeq)) {
          return;
        }
        try {
          sendCallEvent('ice', activeCallRef.current ?? call, { candidate: candidate.toJSON() });
        } catch (error) {
          setCallBootstrapError(getErrorMessage(error));
        }
      },
      onRemoteStream: (stream) => {
        if (!isCallCurrent(call.callId, lifecycleSeq)) {
          return;
        }
        setCallRemoteStream(stream);
        attachMediaStream(remoteCallMediaRef.current, stream);
      },
      onConnectionStateChange: (state) => {
        if (!isCallCurrent(call.callId, lifecycleSeq)) {
          return;
        }
        if (state === 'connected') {
          setCallUiState('connected');
          setActiveCall((prev) => (prev ? { ...prev, state: 'connected' } : prev));
          if (mediaReportedCallIdRef.current !== call.callId) {
            mediaReportedCallIdRef.current = call.callId;
            sendParticipantReport('media_connected');
          }
          if (leaseTimerRef.current === null) {
            leaseTimerRef.current = window.setInterval(
              () => sendParticipantReport('lease_refresh'),
              10_000,
            );
          }
        } else if (state === 'failed' || state === 'disconnected') {
          setCallUiState('error');
        }
      },
    });
    rtcSessionRef.current = rtc;
    const localStream = await rtc.ensureLocalStream();
    if (!isCallCurrent(call.callId, lifecycleSeq)) {
      rtc.close();
      if (rtcSessionRef.current === rtc) {
        rtcSessionRef.current = null;
      }
      throw new Error('Call session is no longer active');
    }
    setCallLocalStream(localStream);
    attachMediaStream(localCallMediaRef.current, localStream);
    return rtc;
  }, [isCallCurrent, pushLog, sendCallEvent, sendParticipantReport]);

  const handleV1Event = useCallback((event: CallEnvelope) => {
    const committed = event.resultCode === 'committed' || event.resultCode === 'already_committed';
    if (event.type === 'error') {
      setCallBootstrapError(CALL_SIGNALING_UNAVAILABLE_MESSAGE);
      pushLog(`通话信令拒绝: ${event.resultCode ?? 'unknown'}`, 'warn');
      return;
    }
    if (event.type === 'result') {
      const current = activeCallRef.current ?? incomingCallRef.current;
      if (!current || (event.commandId && current.commandId !== event.commandId && event.action !== 'media_connected' && event.action !== 'lease_refresh')) return;
      if (!committed || !event.callId || !event.sessionId) {
        if (event.action !== 'media_connected' && event.action !== 'lease_refresh') {
          setCallUiState('error');
          setCallBootstrapError(`通话操作失败：${event.resultCode ?? 'unknown'}`);
        }
        return;
      }
      const updated: CallSession = {
        ...current,
        callId: event.callId,
        sessionId: event.sessionId,
        aggregateVersion: event.aggregateVersion ?? current.aggregateVersion,
        fence: event.fence ?? current.fence,
        capability: event.capability ?? current.capability,
        capabilityExpiresAtMs: event.capabilityExpiresAtMs ?? current.capabilityExpiresAtMs,
      };
      activeCallRef.current = updated;
      incomingCallRef.current = null;
      setActiveCall(updated);
      setIncomingCall(null);
      return;
    }
    if (event.type === 'state') {
      if (!event.callId || !event.sessionId || !event.peerUserId) return;
      const mediaKind = (event.payload as { mediaKind?: CallType })?.mediaKind ?? 'audio';
      if (event.action === 'report_committed') {
        const current = activeCallRef.current;
        if (!current || current.callId !== event.callId) return;
        const updated = {
          ...current,
          aggregateVersion: Math.max(current.aggregateVersion ?? 0, event.aggregateVersion ?? 0),
          fence: event.fence ?? current.fence,
        };
        activeCallRef.current = updated;
        setActiveCall(updated);
        return;
      }
      if (event.action === 'ringing') {
        const existing = activeCallRef.current;
        if (existing?.direction === 'outgoing' && existing.commandId === event.commandId) {
          const updated = { ...existing, callId: event.callId, sessionId: event.sessionId,
            aggregateVersion: event.aggregateVersion, fence: event.fence };
          activeCallRef.current = updated;
          setActiveCall(updated);
          return;
        }
        callLifecycleSeqRef.current += 1;
        resetCallMedia();
        const incoming: CallSession = {
          callId: event.callId,
          commandId: event.commandId ?? nextCommandId(),
          sessionId: event.sessionId,
          aggregateVersion: event.aggregateVersion,
          fence: event.fence,
          reportSequence: 0,
          type: mediaKind,
          direction: 'incoming',
          state: 'incoming',
          peer: {
            userId: event.peerUserId,
            name: contactsRef.current[event.peerUserId]?.name ?? `用户 #${event.peerUserId}`,
            sessionKey: sessionKeyOf('single', event.peerUserId),
          },
        };
        incomingCallRef.current = incoming;
        activeCallRef.current = null;
        setIncomingCall(incoming);
        setActiveCall(null);
        setCallUiState('incoming');
        return;
      }
      if (event.action === 'answered') {
        const current = activeCallRef.current;
        if (!current || current.callId !== event.callId) return;
        const updated = { ...current, state: 'connecting' as const,
          aggregateVersion: event.aggregateVersion ?? current.aggregateVersion,
          fence: event.fence ?? current.fence };
        activeCallRef.current = updated;
        setActiveCall(updated);
        setCallUiState('connecting');
        if (updated.direction === 'outgoing') {
          const lifecycleSeq = callLifecycleSeqRef.current;
          void ensureRtcSession(updated).then((rtc) => rtc.createOffer()).then((offer) => {
            if (isCallCurrent(updated.callId, lifecycleSeq)) sendCallEvent('offer', activeCallRef.current ?? updated, { offer });
          }).catch((error) => {
            if (!isExpectedClosedCallError(error)) setCallBootstrapError(getErrorMessage(error));
          });
        }
        return;
      }
      if (event.action === 'declined' || event.action === 'cancelled' || event.action === 'ended' || event.action === 'busy') {
        finishCall('ended');
      }
      return;
    }
    if (event.type !== 'signal' || !event.callId) return;
    const call = activeCallRef.current;
    if (!call || call.callId !== event.callId) return;
    const payload = event.payload as { offer?: RTCSessionDescriptionInit; answer?: RTCSessionDescriptionInit; candidate?: RTCIceCandidateInit };
    const lifecycleSeq = callLifecycleSeqRef.current;
    if (event.action === 'offer' && payload.offer) {
      void ensureRtcSession(call).then((rtc) => rtc.acceptOffer(payload.offer!)).then((answer) => {
        if (isCallCurrent(call.callId, lifecycleSeq)) sendCallEvent('answer', activeCallRef.current ?? call, { answer });
      }).catch((error) => setCallBootstrapError(getErrorMessage(error)));
    } else if (event.action === 'answer' && payload.answer) {
      void ensureRtcSession(call).then((rtc) => rtc.acceptAnswer(payload.answer!)).catch((error) => setCallBootstrapError(getErrorMessage(error)));
    } else if (event.action === 'ice' && payload.candidate) {
      void ensureRtcSession(call).then((rtc) => rtc.addIceCandidate(payload.candidate!)).catch((error) => setCallBootstrapError(getErrorMessage(error)));
    }
  }, [contactsRef, ensureRtcSession, finishCall, isCallCurrent, pushLog, resetCallMedia, sendCallEvent, sessionKeyOf]);

  useEffect(() => {
    if (authStatus !== 'authenticated' || !authUser) {
      callClientRef.current?.close();
      callClientRef.current = null;
      callLifecycleSeqRef.current += 1;
      activeCallRef.current = null;
      incomingCallRef.current = null;
      resetCallMedia();
      setCallBootstrap(null);
      setCallBootstrapError(null);
      setCallConnectionState('idle');
      setActiveCall(null);
      setIncomingCall(null);
      setCallUiState('idle');
      stopIncomingRingtone();
      return;
    }

    const deviceId = getOrCreateSocketDeviceId();
    const clientSeq = ++callClientSeqRef.current;
    let cancelled = false;

    const startSignaling = () => {
      setCallConnectionState('connecting');

      void fetchCallBootstrap(apiBase, deviceId)
        .then((bootstrap) => {
          if (cancelled || clientSeq !== callClientSeqRef.current) {
            return;
          }
          setCallBootstrap(bootstrap);
          setCallBootstrapError(null);

          const client = new CallSignalingClient(bootstrap, {
            onOpen: () => {
              if (clientSeq !== callClientSeqRef.current || callClientRef.current !== client) {
                return;
              }
              setCallConnectionState('connected');
            },
            onClose: () => {
              if (clientSeq !== callClientSeqRef.current || callClientRef.current !== client) {
                return;
              }
              setCallConnectionState('idle');
            },
            onError: (error) => {
              if (clientSeq !== callClientSeqRef.current || callClientRef.current !== client) {
                return;
              }
              setCallConnectionState('error');
              pushLog(`通话信令后台连接失败: ${error.message}`, 'warn');
            },
            onEvent: (event) => {
              if (clientSeq !== callClientSeqRef.current || callClientRef.current !== client) {
                return;
              }
              handleV1Event(event);
            },
          }, async () => {
            const refreshed = await fetchCallBootstrap(apiBase, deviceId);
            if (!cancelled && clientSeq === callClientSeqRef.current) {
              setCallBootstrap(refreshed);
            }
            return refreshed;
          });

          callClientRef.current?.close();
          callClientRef.current = client;
          setCallConnectionState('connecting');
          client.connect();
        })
        .catch((error) => {
          if (!cancelled) {
            pushLog(`通话信令后台初始化失败: ${getErrorMessage(error)}`, 'warn');
            setCallConnectionState('error');
          }
        });
    };

    startSignaling();

    return () => {
      cancelled = true;
      if (callClientRef.current) {
        callClientRef.current.close();
        callClientRef.current = null;
      }
      callLifecycleSeqRef.current += 1;
      activeCallRef.current = null;
      incomingCallRef.current = null;
      resetCallMedia();
      stopIncomingRingtone();
    };
  }, [apiBase, authStatus, authUser, contactsRef, ensureRtcSession, finishCall, handleV1Event, isCallCurrent, pushLog, resetCallMedia, sendCallEvent, sessionKeyOf, stopIncomingRingtone]);

  const handleStartCall = useCallback((type: CallType, explicitPeer?: CallPeer) => {
    const client = callClientRef.current;
    const session = activeSessionKeyRef.current
      ? sessionsRef.current[activeSessionKeyRef.current] ?? null
      : null;

    if (!client || !callBootstrap || (!explicitPeer && (!session || session.sessionType !== 'single'))) {
      pushLog('当前无法发起通话', 'warn');
      setCallBootstrapError(CALL_SIGNALING_UNAVAILABLE_MESSAGE);
      return;
    }

    const peer = explicitPeer ?? {
      userId: session!.sessionId,
      name: session!.title || contactsRef.current[session!.sessionId]?.name || `用户 #${session!.sessionId}`,
      sessionKey: session!.sessionKey,
    };
    const commandId = nextCommandId();
    const nextSession: CallSession = {
      callId: commandId,
      commandId,
      reportSequence: 0,
      type,
      direction: 'outgoing',
      state: 'dialing',
      peer,
    };

    try {
      const envelope: CallEnvelope<{ mediaKind: CallType }> = {
        protocolVersion: 1,
        type: 'command',
        action: 'create',
        commandId,
        peerUserId: peer.userId,
        payload: { mediaKind: type },
      };
      callLifecycleSeqRef.current += 1;
      activeCallRef.current = nextSession;
      incomingCallRef.current = null;
      setIncomingCall(null);
      setActiveCall(nextSession);
      setCallUiState('dialing');
      client.send(envelope);
    } catch (error) {
      activeCallRef.current = null;
      setActiveCall(null);
      setCallUiState('error');
      setCallBootstrapError(CALL_SIGNALING_UNAVAILABLE_MESSAGE);
      pushLog(`发起通话失败: ${getErrorMessage(error)}`, 'error');
    }
  }, [activeSessionKeyRef, authUser, callBootstrap, contactsRef, pushLog, sessionsRef]);

  const handleRespondIncomingCall = useCallback((accepted: boolean) => {
    const client = callClientRef.current;
    if (!client || !callBootstrap || !incomingCall) {
      return;
    }

    try {
      if (!incomingCall.sessionId || !incomingCall.aggregateVersion || !incomingCall.fence) {
        throw new Error('Incoming call ownership state is incomplete');
      }
      const commandId = nextCommandId();
      client.send({
        protocolVersion: 1,
        type: 'command',
        action: accepted ? 'answer' : 'decline',
        commandId,
        callId: incomingCall.callId,
        sessionId: incomingCall.sessionId,
        aggregateVersion: incomingCall.aggregateVersion,
        fence: incomingCall.fence,
        payload: {},
      });
      if (accepted) {
        callLifecycleSeqRef.current += 1;
        const nextCall = { ...incomingCall, commandId, state: 'connecting' as const };
        activeCallRef.current = nextCall;
        incomingCallRef.current = null;
        setActiveCall(nextCall);
        void ensureRtcSession(nextCall).catch((error) => {
          if (isExpectedClosedCallError(error)) {
            return;
          }
          setCallUiState('error');
          setCallBootstrapError(CALL_SIGNALING_UNAVAILABLE_MESSAGE);
        });
        setCallUiState('connecting');
      } else {
        finishCall('idle');
      }
      setIncomingCall(null);
      stopIncomingRingtone();
    } catch (error) {
      setCallBootstrapError(CALL_SIGNALING_UNAVAILABLE_MESSAGE);
      pushLog(`响应通话失败: ${getErrorMessage(error)}`, 'error');
      setCallUiState('error');
    }
  }, [callBootstrap, ensureRtcSession, finishCall, incomingCall, stopIncomingRingtone]);

  const handleHangupCall = useCallback(() => {
    const client = callClientRef.current;
    if (!client || !callBootstrap || !activeCall) {
      finishCall('idle');
      return;
    }

    try {
      if (!activeCall.sessionId || !activeCall.aggregateVersion || !activeCall.fence) {
        throw new Error('Active call ownership state is incomplete');
      }
      client.send({
        protocolVersion: 1,
        type: 'command',
        action: activeCall.state === 'dialing' ? 'cancel' : 'hangup',
        commandId: nextCommandId(),
        callId: activeCall.callId,
        sessionId: activeCall.sessionId,
        aggregateVersion: activeCall.aggregateVersion,
        fence: activeCall.fence,
        payload: {},
      });
    } catch (error) {
      setCallBootstrapError(getErrorMessage(error));
    }

    finishCall('idle');
  }, [activeCall, callBootstrap, finishCall]);

  const handleToggleMute = useCallback(() => {
    setIsMuted((prev) => {
      const next = !prev;
      if (callLocalStream) {
        callLocalStream.getAudioTracks().forEach((track) => {
          track.enabled = !next;
        });
      }
      return next;
    });
  }, [callLocalStream]);

  const handleToggleVideo = useCallback(() => {
    setIsVideoEnabled((prev) => {
      const next = !prev;
      if (callLocalStream) {
        callLocalStream.getVideoTracks().forEach((track) => {
          track.enabled = next;
        });
      }
      return next;
    });
  }, [callLocalStream]);

  return {
    callBootstrap,
    callBootstrapError,
    callConnectionState,
    callUiState,
    activeCall,
    incomingCall,
    isMuted,
    isVideoEnabled,
    callAudioLevels: {
      local: localAudioLevel,
      remote: remoteAudioLevel,
      localSpeaking: localAudioLevel >= 0.08,
      remoteSpeaking: remoteAudioLevel >= 0.08,
    },
    localCallMediaRef,
    remoteCallMediaRef,
    callLocalStream,
    callRemoteStream,
    handleStartCall,
    handleRespondIncomingCall,
    handleHangupCall,
    handleToggleMute,
    handleToggleVideo,
  };
}
